/**
 * 인증 상태 관리 Hook
 *
 * TanStack Query를 사용한 중앙화된 인증 상태 관리입니다.
 * 사용자 정보, 로그인/로그아웃, 회원가입 등의 기능을 제공합니다.
 *
 * 데이터 흐름:
 * 1. 앱 로드 → useMe() 실행 (토큰이 있으면 사용자 정보 가져옴)
 * 2. 로그인 → useLogin mutation → token 저장 + invalidateQueries
 * 3. invalidateQueries → useMe refetch → user 정보 업데이트
 * 4. isAuthenticated = useMe().data !== null
 *
 * @hook
 * @example
 * // 기본 사용 (자동으로 /users/me 호출)
 * const { user, isAuthenticated, login, logout } = useAuth();
 *
 * // 로그인 폼 (enableMe: false로 불필요한 호출 방지)
 * const { login, isLoggingIn, error } = useAuth({ enableMe: false });
 *
 * @param {object} [options] - 설정 옵션
 * @param {boolean} [options.enableMe=true] - useMe 쿼리 활성화 여부
 *   (로그인/회원가입 페이지에서는 false로 설정하여 불필요한 API 호출 방지)
 *
 * @returns {object} 인증 상태 및 메서드
 * @returns {User | null} user - 현재 로그인된 사용자 정보
 * @returns {boolean} isAuthenticated - 인증 상태
 * @returns {boolean} loading - 사용자 정보 로딩 중
 * @returns {string | null} error - 에러 메시지
 * @returns {(credentials: LoginRequest) => Promise<void>} login - 로그인 함수
 * @returns {boolean} isLoggingIn - 로그인 진행 중
 * @returns {(credentials: RegisterRequest) => Promise<void>} register - 회원가입 함수
 * @returns {boolean} isRegistering - 회원가입 진행 중
 * @returns {() => void} logout - 로그아웃 함수
 * @returns {() => void} clearError - 에러 메시지 초기화
 */

'use client';

import { useQueryClient } from '@tanstack/react-query';
import { invalidateUserMe } from '@/lib/api/cacheUtils';
import {
  useUserMe,
  useAuthLogin,
  useAuthRegister,
  useAuthLogout,
} from '@/lib/api/orval';
import { setTokens, clearTokens } from '@/lib/auth/cookies';

/**
 * 인증 API 응답 타입
 * Backend가 로그인/회원가입 시 반환하는 토큰 데이터
 * (Orval이 void로 생성하므로 별도 정의 필요)
 */
interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string;
}

/**
 * 타입 가드: 객체가 AuthTokenResponse인지 확인
 */
function isAuthTokenResponse(data: unknown): data is AuthTokenResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'accessToken' in data &&
    'refreshToken' in data &&
    typeof (data as AuthTokenResponse).accessToken === 'string' &&
    typeof (data as AuthTokenResponse).refreshToken === 'string'
  );
}

interface UseAuthOptions {
  /**
   * useMe 쿼리 활성화 여부
   * @default true
   * @note 로그인/회원가입 페이지에서는 false로 설정하여 불필요한 API 호출 방지
   */
  enableMe?: boolean;
}

export function useAuth(options?: UseAuthOptions) {
  const queryClient = useQueryClient();

  // useMe는 옵션에 따라 활성화 (기본값: true)
  // 로그인/회원가입 페이지에서는 false로 설정하여 로딩 상태 방지
  const meQuery = useUserMe({
    query: {
      enabled: options?.enableMe ?? true,
      retry: 1, // 1회 재시도 (네트워크 일시적 장애 대응, 인증 실패 시 빠른 감지)
    },
  });

  // 인증 mutations (Orval 생성 훅 사용)
  const loginMutation = useAuthLogin({
    mutation: {
      onSuccess: (data) => {
        // 토큰 저장 (Backend는 camelCase로 accessToken, refreshToken 반환)
        // 타입 가드를 사용하여 안전하게 토큰 추출
        if (isAuthTokenResponse(data)) {
          setTokens(data.accessToken, data.refreshToken);
          // 사용자 정보 다시 가져오기
          invalidateUserMe(queryClient);
        }
      },
    },
  });

  const registerMutation = useAuthRegister({
    mutation: {
      onSuccess: (data) => {
        // 회원가입 후 토큰이 있으면 저장 (Backend는 camelCase로 반환)
        // 타입 가드를 사용하여 안전하게 토큰 추출
        if (isAuthTokenResponse(data)) {
          setTokens(data.accessToken, data.refreshToken);
          invalidateUserMe(queryClient);
        }
      },
    },
  });

  const logoutMutation = useAuthLogout({
    mutation: {
      onSuccess: () => {
        // 토큰 삭제
        clearTokens();
        // 캐시 초기화
        queryClient.clear();
      },
      onError: () => {
        // 로그아웃 실패해도 로컬 토큰은 삭제
        clearTokens();
        queryClient.clear();
      },
    },
  });

  // 에러 상태 (모든 작업의 에러를 종합)
  const error = meQuery.error || loginMutation.error || registerMutation.error;

  // 래핑된 로그인 함수 (폼에서 { email, password }로 호출 가능)
  const login = async (credentials: { email: string; password: string }) => {
    return loginMutation.mutateAsync({
      data: credentials,
    });
  };

  // 래핑된 회원가입 함수 (폼에서 { email, password, ... }로 호출 가능)
  // PR-2e: acceptTerms/acceptPrivacy boolean 필드 지원
  const register = async (
    credentials: { email: string; password: string } & Record<string, string | boolean>,
  ) => {
    return registerMutation.mutateAsync({
      data: credentials,
    });
  };

  return {
    // 사용자 정보 (Query 캐시에서 가져옴)
    user: meQuery.data || null,

    // 로그인 여부 (query가 성공했고 data가 있으면 authenticated)
    isAuthenticated: meQuery.isSuccess && meQuery.data !== null,

    // 로딩 상태
    isLoadingUser: meQuery.isLoading,
    loading: meQuery.isLoading,

    // 에러 상태
    error: error ? (error instanceof Error ? error.message : String(error)) : null,

    // 로그인 Mutation
    login,
    isLoggingIn: loginMutation.isPending,

    // 회원가입 Mutation
    register,
    isRegistering: registerMutation.isPending,

    // 로그아웃 Mutation
    logout: () => {
      logoutMutation.mutate({ data: {} as never });
    },
    isLoggingOut: logoutMutation.isPending,

    // 에러 초기화
    clearError: () => {
      // TanStack Query는 작업 완료 후 자동으로 에러를 클리어함
      // 필요시 수동으로 클리어하려면 queryClient 사용
    },
  };
}
